import { useEffect, useState } from 'react'

/** Tailwind `sm` / `lg` / `xl` (default theme). */
const GRID_BREAKPOINTS = {
  sm: '(min-width: 640px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
} as const

/** Item-card grid matching the `items` column count (3 below `xl`, then 4). */
export const STOREFRONT_GRID_CLASS = 'grid grid-cols-3 gap-2 sm:gap-4 xl:grid-cols-4'

/** Rows of cards each home-page section previews (Clearance & deals and every category). */
export const HOME_SECTION_ROWS = 4

export type StorefrontGridMode = 'storefront' | 'items'

function readColumnCount(mode: StorefrontGridMode): number {
  if (typeof window === 'undefined') return 1
  const mqSm = window.matchMedia(GRID_BREAKPOINTS.sm)
  const mqLg = window.matchMedia(GRID_BREAKPOINTS.lg)
  const mqXl = window.matchMedia(GRID_BREAKPOINTS.xl)
  if (mqXl.matches) return 4
  if (mode === 'items') return 3
  if (mqLg.matches) return 3
  if (mqSm.matches) return 2
  return 1
}

/**
 * Column count for responsive product grids.
 * - `storefront`: matches clearance rail — 1 col below `sm`, then 2 / 3 / 4.
 * - `items`: matches `/items` — 3 cols below `xl`, then 4.
 */
export function useStorefrontGridColumnCount(mode: StorefrontGridMode = 'storefront'): number {
  const [cols, setCols] = useState(() => readColumnCount(mode))

  useEffect(() => {
    const mqSm = window.matchMedia(GRID_BREAKPOINTS.sm)
    const mqLg = window.matchMedia(GRID_BREAKPOINTS.lg)
    const mqXl = window.matchMedia(GRID_BREAKPOINTS.xl)

    const update = () => setCols(readColumnCount(mode))

    update()
    mqSm.addEventListener('change', update)
    mqLg.addEventListener('change', update)
    mqXl.addEventListener('change', update)
    return () => {
      mqSm.removeEventListener('change', update)
      mqLg.removeEventListener('change', update)
      mqXl.removeEventListener('change', update)
    }
  }, [mode])

  return cols
}
